import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ICompany, ICompanyDetails} from '@models/company.interface';
import {ICompteAdministration} from '@models/compteAdministration.interface';
import {CompanyDataService} from '@services/company-data.service';
import {CompanyService} from '@services/company.service';
import {NotificationService} from '@services/frontend/notification.service';

@Component({
  selector: 'app-company-entreprise',
  templateUrl: './company-entreprise.html',
})
export class CompanyEntreprise implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyData = inject(CompanyDataService);
  private readonly companyService = inject(CompanyService);
  private readonly notificationService = inject(NotificationService);

  compteAdministration: WritableSignal<ICompteAdministration | null> = signal(null);
  companyDetails: WritableSignal<ICompanyDetails> = signal({} as ICompanyDetails);
  company: WritableSignal<ICompany> = signal({} as ICompany);
  copiedFieldIndex: WritableSignal<number | null> = signal(null);
  pushing: WritableSignal<boolean> = signal(false);

  private currentSiret : string = '';

  ngOnInit() {
    const siret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.currentSiret = siret;
    this.companyData.load(siret).subscribe(compte => {
      this.compteAdministration.set(compte);
      this.company.set(compte.company);
      this.companyDetails.set(compte.company.companyDetailResponse);
    });
  }

  pushWallet(): void {
    if (this.pushing()) return;
    this.pushing.set(true);
    this.companyService.pushWalletUpdate(this.currentSiret).subscribe({
      next: () => {
        this.pushing.set(false);
        this.notificationService.success('Mise à jour envoyée aux Apple Wallet', 2500);
      },
      error: () => {
        this.pushing.set(false);
        this.notificationService.error('Erreur lors de l\'envoi vers Apple Wallet', 3000);
      }
    });
  }

  companyInitials(): string {
    const name = this.companyDetails().nomEntreprise || 'Mon entreprise';
    return name
      .split(' ')
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() || '')
      .join('');
  }

  infoFields(): { icon: string; label: string; value: string }[] {
    const d = this.companyDetails();
    return [
      {icon: 'pin_drop', label: 'SIRET', value: this.company().siret || '—'},
      {icon: 'badge', label: 'SIREN', value: d.siren || '—'},
      {icon: 'description', label: 'Forme juridique', value: d.formeJuridique || '—'},
      {icon: 'work', label: 'Code APE / NAF', value: d.codeNaf || '—'},
      {icon: 'receipt_long', label: 'N° TVA intracommunautaire', value: d.numeroTvaIntracommunautaire || '—'},
      {icon: 'gavel', label: 'RCS', value: d.numeroRcs || '—'},
      {icon: 'paid', label: 'Capital social', value: d.capitalFormate || '—'},
      {
        icon: 'person',
        label: 'Dirigeant',
        value: d.dirigeant ? d.dirigeant : '—'
      },
      {icon: 'calendar_month', label: 'Date de création', value: d.dateCreation ? new Date(d.dateCreation).toLocaleDateString('fr-FR') : '—'}
    ];
  }

  copyField(value: string, index: number) {
    if (!value || value === '—') {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.copiedFieldIndex.set(index);
      this.notificationService.success('Copié !', 1200);
      setTimeout(() => this.copiedFieldIndex.set(null), 1500);
    });
  }
}
